import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Directive, ElementRef, inject, PLATFORM_ID } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import type { Timer } from '@freelancer/time-utils';
import { TimeUtils } from '@freelancer/time-utils';

const AUTO_HIDE_DELAY_MS = 4000;

/**
 * Toggles a tooltip on tap on a device with no hover pointer. Material opens a
 * tooltip on hover, and a browser sends one hover enter for the first tap and
 * none for the taps after it, so the tooltip never opens a second time.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[matTooltip]',
  standalone: true,
})
export class TooltipTapDirective implements OnInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly timeUtils = inject(TimeUtils);
  private readonly tooltip = inject(MatTooltip);

  private autoHideTimeout?: Timer;
  private enabled = false;
  private visible = false;

  ngOnInit(): void {
    // A coarse pointer as well as no hover, because a headless browser reports
    // no hover and no pointer at all, and must keep the hover behaviour.
    if (
      !isPlatformBrowser(this.platformId) ||
      !window.matchMedia('(hover: none) and (pointer: coarse)').matches
    ) {
      return;
    }

    this.enabled = true;

    // Material binds its own listeners in ngAfterViewInit, which runs after this,
    // so blocking the hover events here leaves the tooltip to the toggle below.
    this.tooltip.touchGestures = 'off';
    const element = this.element.nativeElement;
    element.addEventListener('mouseenter', this.stopHoverEvent);
    element.addEventListener('mouseleave', this.stopHoverEvent);
    element.addEventListener('click', this.toggle);
  }

  ngOnDestroy(): void {
    // ngOnInit adds nothing on the server or on a device with a hover pointer.
    if (!this.enabled) {
      return;
    }

    const element = this.element.nativeElement;
    element.removeEventListener('mouseenter', this.stopHoverEvent);
    element.removeEventListener('mouseleave', this.stopHoverEvent);
    element.removeEventListener('click', this.toggle);
    this.hide();
  }

  private readonly stopHoverEvent = (event: Event): void => {
    event.stopImmediatePropagation();
  };

  private readonly toggle = (): void => {
    if (this.visible) {
      this.hide();
      return;
    }

    this.tooltip.show(0);
    this.visible = true;
    this.autoHideTimeout = this.timeUtils.setTimeout(
      () => this.hide(),
      AUTO_HIDE_DELAY_MS,
    );
    this.document.addEventListener('pointerdown', this.hideOnOutsideTap, true);
  };

  private readonly hideOnOutsideTap = (event: PointerEvent): void => {
    const { target } = event;
    if (target instanceof Node && this.element.nativeElement.contains(target)) {
      return;
    }

    this.hide();
  };

  private hide(): void {
    if (this.autoHideTimeout !== undefined) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = undefined;
    }

    this.document.removeEventListener(
      'pointerdown',
      this.hideOnOutsideTap,
      true,
    );
    this.tooltip.hide(0);
    this.visible = false;
  }
}
